import SakuraFrame from './frames/SakuraFrame';
import PinkNeonFrame from './frames/PinkNeonFrame';
import PurpleHudFrame from './frames/PurpleHudFrame';
import TokyoExpresswayFrame from './frames/TokyoExpresswayFrame';
import LiquidGlassFrame from './frames/LiquidGlassFrame';
import RedlineFrame from './frames/RedlineFrame';
import StreetNeonFrame from './frames/StreetNeonFrame';
import HeadUnitFrame from './frames/HeadUnitFrame';
import BlackDeckFrame from './frames/BlackDeckFrame';
import VipFrame from './frames/VipFrame';
import GoldOrnateFrame from './frames/GoldOrnateFrame';
import BlueprintLayout from './frames/BlueprintLayout';
import LowriderFrame from './frames/LowriderFrame';
import RallyFrame from './frames/RallyFrame';
import HoloPassFrame from './frames/HoloPassFrame';
import JewelCaseFrame from './frames/JewelCaseFrame';
import BootHudLayout from './frames/BootHudLayout';

// Web port of app/components/eventAesthetics/registry.js — same data + display
// order; only the platform font shim differs (web serif stack vs RN Platform).
// Every frame reserves the same bleed so a themed card can claim a fixed margin
// for the parts of the frame that extend past the card box.
export const EVENT_AESTHETIC_BLEED = 12;

const serif = 'Georgia, "Times New Roman", serif';

// A token descriptor per aesthetic. The card layout/size never changes — these
// only restyle colours and overlay a decorative SVG Frame.
//   accent        – stripe + RSVP badge + count icon
//   badgeColor    – category badge tint   badgeTextColor – its text
//   surface       – card background override (subtle themed dark)
//   titleStyle / metaStyle – text overrides
//   swatch        – two-colour chip for the picker button + grid
const EVENT_AESTHETICS_SOURCE = [
  {
    id: 'none',
    name: 'None',
    subtitle: 'Default card',
    accent: '#8892b0',
    Frame: null,
    swatch: ['#3a3a3c', '#1c1c1e'],
  },
  {
    id: 'sakura',
    name: 'Sakura',
    subtitle: 'Cherry blossom',
    accent: '#e88aa0',
    badgeColor: '#e88aa0',
    badgeTextColor: '#ffd9e2',
    surface: '#1a1416',
    bleed: 16, // blossom garland rings the border into a wider bleed (matches SakuraFrame BLEED)
    titleStyle: { fontFamily: serif, color: '#fbe3ea', fontWeight: '600' },
    metaColor: '#e9b9c6',
    metaStyle: { color: '#e9b9c6' },
    Frame: SakuraFrame,
    swatch: ['#f4c9d4', '#e88aa0'],
  },
  {
    id: 'pinkNeon',
    name: 'Pink Neon',
    subtitle: 'Magenta glow',
    accent: '#ff5cf2',
    badgeColor: '#ff5cf2',
    badgeTextColor: '#ffd6fb',
    surface: '#16101a',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#e3a9d8',
    metaStyle: { color: '#e3a9d8' },
    Frame: PinkNeonFrame,
    swatch: ['#ff8af6', '#ff2ee6'],
  },
  {
    id: 'purpleHud',
    name: 'Purple HUD',
    subtitle: 'Cyber tech',
    accent: '#a06bff',
    badgeColor: '#a06bff',
    badgeTextColor: '#ddccff',
    surface: '#100f1a',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#b3a4d8',
    metaStyle: { color: '#b3a4d8' },
    Frame: PurpleHudFrame,
    swatch: ['#b98aff', '#7a3dff'],
  },
  {
    id: 'tokyoExpressway',
    name: 'Shuto Expressway',
    subtitle: 'Tokyo signage',
    accent: '#2faf6a',
    badgeColor: '#2faf6a',
    badgeTextColor: '#bfe9cf',
    surface: '#0c1411',
    titleStyle: { color: '#eaf2ee', fontWeight: '800' },
    metaColor: '#a8c2b3',
    metaStyle: { color: '#a8c2b3' },
    Frame: TokyoExpresswayFrame,
    swatch: ['#2faf6a', '#2f6db0'],
  },
  {
    id: 'liquidGlass',
    name: 'Liquid Glass',
    subtitle: 'Water',
    accent: '#7fd4d0',
    badgeColor: '#7fd4d0',
    badgeTextColor: '#d6f5f2',
    surface: '#0e1718',
    titleStyle: { color: '#eafaf8', fontWeight: '700' },
    metaColor: '#a9d0cd',
    metaStyle: { color: '#a9d0cd' },
    Frame: LiquidGlassFrame,
    swatch: ['#cdeeea', '#5fb8b2'],
  },
  {
    id: 'redline',
    name: 'Redline',
    subtitle: 'Dyno chart',
    accent: '#ff3b3b',
    badgeColor: '#ff3b3b',
    badgeTextColor: '#ffd2d2',
    surface: '#140a0c',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#c9b6b8',
    metaStyle: { color: '#c9b6b8' },
    Frame: RedlineFrame,
    swatch: ['#ff6b6b', '#4a9eff'],
  },
  {
    id: 'streetNeon',
    name: 'Street Neon',
    subtitle: 'Neon convoy',
    accent: '#2bff88',
    badgeColor: '#2bff88',
    badgeTextColor: '#c9ffe0',
    surface: '#0b1510',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#a8d8bd',
    metaStyle: { color: '#a8d8bd' },
    Frame: StreetNeonFrame,
    swatch: ['#7dffb0', '#1fdb6e'],
  },
  {
    id: 'headUnit',
    name: 'Head Unit',
    subtitle: 'Crystal blue',
    accent: '#3a9fe6',
    badgeColor: '#3a9fe6',
    badgeTextColor: '#cfe6ff',
    surface: '#0a1622',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#9fc4e0',
    metaStyle: { color: '#9fc4e0' },
    Frame: HeadUnitFrame,
    swatch: ['#8fd0ff', '#1f6fce'],
  },
  {
    id: 'blackDeck',
    name: 'Black Deck',
    subtitle: 'Dark stereo',
    accent: '#35d0e0',
    badgeColor: '#35d0e0',
    badgeTextColor: '#cdf3f8',
    surface: '#0a0e12',
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#9fb8bd',
    metaStyle: { color: '#9fb8bd' },
    Frame: BlackDeckFrame,
    swatch: ['#5fd6e2', '#1a2027'],
  },
  {
    id: 'vip', name: 'VIP', subtitle: 'All access',
    accent: '#d8c08a', badgeColor: '#d8c08a', badgeTextColor: '#f3e8c8',
    surface: '#0b0a10',
    titleStyle: { color: '#f3ecdc', fontWeight: '800' },
    metaColor: '#c3b48f', metaStyle: { color: '#c3b48f' },
    Frame: VipFrame, swatch: ['#b8a0ff', '#d8c08a'],
  },
  {
    id: 'goldOrnate', name: 'Gold Royale', subtitle: 'Fine gold line',
    accent: '#e6c66a', badgeColor: '#e6c66a', badgeTextColor: '#f6e7b0',
    surface: '#0c0a06',
    bleed: 6, // slim border sits right at the card edge (matches GoldOrnateFrame BLEED)
    titleStyle: { fontFamily: serif, color: '#f6ecd0', fontWeight: '700' },
    metaColor: '#c8b483', metaStyle: { color: '#c8b483' },
    Frame: GoldOrnateFrame, swatch: ['#f0d98a', '#b8862f'],
  },
  {
    id: 'blueprint', name: 'Blueprint', subtitle: 'Console HUD',
    accent: '#3a7bff', badgeColor: '#3a7bff', badgeTextColor: '#cfe0ff',
    surface: '#0a1428',
    titleStyle: { color: '#eaf1ff', fontWeight: '800' },
    metaColor: '#9fb6e0', metaStyle: { color: '#9fb6e0' },
    Layout: BlueprintLayout, swatch: ['#7fb0ff', '#1b3a8f'],
  },
  {
    id: 'lowrider', name: 'Lowrider', subtitle: 'Polished chrome',
    accent: '#c2c7d2', badgeColor: '#c2c7d2', badgeTextColor: '#eef1f6',
    surface: '#08080b',
    bleed: 14, // chrome crest / bumper bullets / tail-fins extend outside (matches LowriderFrame BLEED)
    titleStyle: { color: '#f2f2f5', fontWeight: '800' },
    metaColor: '#b8b8c0', metaStyle: { color: '#b8b8c0' },
    Frame: LowriderFrame, swatch: ['#f4f6fa', '#5c6470'],
  },
  {
    id: 'rally', name: 'Rally', subtitle: 'Pace notes',
    accent: '#f2c14e', badgeColor: '#f2c14e', badgeTextColor: '#f7e3a8',
    surface: '#15140f',
    bleed: 16, // roadbook side ruler + pace-note column live in a wider bleed (matches RallyFrame BLEED)
    titleStyle: { color: '#f3efe4', fontWeight: '800' },
    metaColor: '#bcb59a', metaStyle: { color: '#bcb59a' },
    Frame: RallyFrame, swatch: ['#f2c14e', '#2a2622'],
  },
  {
    id: 'holoPass', name: 'Holo Pass', subtitle: 'All access',
    accent: '#bfa3ff', badgeColor: '#bfa3ff', badgeTextColor: '#e7dcff',
    surface: '#070708',
    bleed: 14, // lanyard slot pokes above the top edge (matches HoloPassFrame BLEED)
    titleStyle: { color: '#ffffff', fontWeight: '800' },
    metaColor: '#b9c2e0', metaStyle: { color: '#b9c2e0' },
    Frame: HoloPassFrame, swatch: ['#bfa3ff', '#6fe9d6'],
  },
  {
    id: 'jewelCase', name: 'Jewel Case', subtitle: 'Clear disc',
    accent: '#cfd6e0', badgeColor: '#cfd6e0', badgeTextColor: '#eef2f7',
    surface: '#060608',
    titleStyle: { color: '#ffffff', fontWeight: '700' },
    metaColor: '#aab2bd', metaStyle: { color: '#aab2bd' },
    Frame: JewelCaseFrame, swatch: ['#e9edf2', '#9bb8ff'],
  },
  {
    id: 'bootHud', name: 'Boot Disc', subtitle: 'Console 2000',
    accent: '#2f86ff', badgeColor: '#2f86ff', badgeTextColor: '#cfe0ff',
    surface: '#05091a',
    titleStyle: { color: '#dbe8ff', fontWeight: '800' },
    metaColor: '#8fb4ff', metaStyle: { color: '#8fb4ff' },
    Layout: BootHudLayout, swatch: ['#4aa0ff', '#0a1f4d'],
  }
];

// Picker display order: pinned ids first, then the rest in reverse of definition
// order.
const PINNED = ['none'];
const _aePinned = PINNED.map((id) => EVENT_AESTHETICS_SOURCE.find((a) => a.id === id)).filter(Boolean);
const _aeRest = EVENT_AESTHETICS_SOURCE.filter((a) => !PINNED.includes(a.id)).reverse();
export const EVENT_AESTHETICS = [..._aePinned, ..._aeRest];

const BY_ID = EVENT_AESTHETICS.reduce((acc, a) => { acc[a.id] = a; return acc; }, {});

// A real, frame-bearing aesthetic, or null for none/unknown. Callers use the
// null result to fall back to the plain default card.
export function getEventAesthetic(id) {
  if (!id || id === 'none') return null;
  return BY_ID[id] || null;
}

// Any descriptor including 'none' — used by the picker so it can preview the
// default look alongside the themed ones.
export function getEventAestheticOrNone(id) {
  return BY_ID[id] || BY_ID.none;
}
